import React from "react";
import { Link } from "react-router-dom";
import { Check, User, Sparkles, TrendingUp, Plus } from "lucide-react";
import { useContent } from "../hooks/useContent";
import { useLang } from "../context/LanguageContext";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/motion/Reveal";
import { PackageCard } from "../components/cards/PackageCard";
import { CTASection } from "../components/home/FinancialBenefits";

export default function Packages() {
  const { t, tc } = useLang();
  const { data: packages } = useContent("packages");

  return (
    <>
      <SEO pageKey="packages" title={t.seo.packages.title} description={t.seo.packages.description} />
      <PageHeader pageKey="packages"
        overline={t.packages.overline}
        title={t.packages.title}
        sub={t.packages.sub}
        image="https://images.unsplash.com/photo-1761983721171-bb86a1aeb002?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      <section className="py-10 sm:py-14 bg-lightgray">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-3 gap-0 lg:gap-8 items-center">
            {packages.map((p) => <PackageCard key={p.slug} pkg={p} />)}
          </div>
        </div>
      </section>

      {/* Detailed breakdown */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 space-y-12">
          {packages.map((p, idx) => (
            <Reveal key={p.slug}>
              <div className="grid lg:grid-cols-12 gap-10" data-testid={`package-detail-${p.slug}`}>
                <div className="lg:col-span-4">
                  <span className="overline">0{p.order}</span>
                  <h2 className="font-display text-3xl text-charcoal mt-3 mb-4">{tc(p.name)}</h2>
                  <p className="text-charcoal/60 leading-relaxed mb-6">{tc(p.overview)}</p>
                  <div className="bg-lightgray rounded-2xl p-5">
                    <div className="flex items-center gap-2 text-green mb-2"><User className="w-4 h-4" /> <span className="text-sm font-semibold">{t.packages.idealBuyer}</span></div>
                    <p className="text-charcoal/60 text-sm">{tc(p.ideal_buyer)}</p>
                  </div>
                </div>
                <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
                  {[
                    { icon: TrendingUp, label: t.packages.investmentBenefits, items: tc(p.investment_benefits) },
                    { icon: Sparkles, label: t.packages.lifestyleBenefits, items: tc(p.lifestyle_benefits) },
                    { icon: Plus, label: t.packages.optionalServices, items: tc(p.optional_services) },
                  ].map((col, i) => (
                    <div key={i} className="border border-border rounded-2xl p-6">
                      <col.icon className="w-5 h-5 text-gold mb-4" />
                      <h3 className="font-display text-lg text-charcoal mb-4">{col.label}</h3>
                      <ul className="space-y-2.5">
                        {(Array.isArray(col.items) ? col.items : []).map((it, j) => (
                          <li key={j} className="flex items-start gap-2 text-charcoal/65 text-sm"><Check className="w-3.5 h-3.5 text-green mt-1 shrink-0" /> {it}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
